(() => {
  function getCurrencyCode() {
    const htmlCur = document.documentElement.getAttribute('data-shop-currency');
    if (htmlCur) return htmlCur;
    if (window.Shopify && (Shopify.currency?.active || Shopify.currency)) {
      return Shopify.currency.active || Shopify.currency;
    }
    const meta = document.querySelector('meta[name="shop-currency"]');
    if (meta?.content) return meta.content;
    return 'USD';
  }

  function formatCurrencyNumber(value) {
    const currency = getCurrencyCode();
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0
      }).format(value);
    } catch (_) {
      return (Number(value) || 0).toLocaleString();
    }
  }

  function lockBodyScroll() {
    const b = document.body;
    if (b.dataset.prevOverflow === undefined) {
      b.dataset.prevOverflow = b.style.overflow || '';
    }
    b.style.overflow = 'hidden';
  }

  function unlockBodyScroll() {
    const b = document.body;
    if (b.dataset.prevOverflow !== undefined) {
      b.style.overflow = b.dataset.prevOverflow;
      delete b.dataset.prevOverflow;
    } else {
      b.style.overflow = '';
    }
  }

  let cfObserver = null;
  function observeCollectionFilters() {
    cfObserver?.disconnect?.();
    const cf = document.querySelector('.collection-filters');
    if (!cf) return;
    cfObserver = new MutationObserver((muts) => {
      muts.forEach((m) => {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          if (cf.classList.contains('open_filter')) lockBodyScroll();
          else unlockBodyScroll();
        }
      });
    });
    cfObserver.observe(cf, { attributes: true, attributeFilter: ['class'] });
    if (cf.classList.contains('open_filter')) lockBodyScroll();
    else unlockBodyScroll();
  }

  if (!customElements.get('accordion-toggle')) {
    class AccordionToggle extends HTMLElement {
      connectedCallback() {
        this.header = this.querySelector('.accordion__header');
        this.content = this.querySelector('.accordion__content');
        if (this.header) {
          this.header.addEventListener('click', () => {
            this.classList.toggle('accordion--open');
            if (this.content) {
              this.style.setProperty('--accordion-content-height', `${this.content.scrollHeight}px`);
            }
          });
        }
      }
    }
    customElements.define('accordion-toggle', AccordionToggle);
  }

  if (!customElements.get('collection-filters')) {
    class CollectionFilters extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.getAttribute('data-section-id');
        this.formEl = this.querySelector('form.collection-filters__content');
        this.defaultSort = this.formEl?.dataset.sortDefault || '';
        this.applyBtn = this.querySelector('.filter_action_btn.apply');
        this.clearBtn = this.querySelector('.filter_action_btn.clear');
        this.checkboxInputs = Array.from(this.querySelectorAll('input[type="checkbox"]:not([data-sort-mobile])'));
        this.minRange = this.querySelector('input[data-min-value]');
        this.maxRange = this.querySelector('input[data-max-value]');
        this.minValueDisplay = this.querySelector('[data-min-value-display]');
        this.maxValueDisplay = this.querySelector('[data-max-value-display]');
        this.rangeWrap = this.querySelector('.collection-filter__price-range');
        this.sortChecks = Array.from(this.querySelectorAll('input[type="checkbox"][data-sort-mobile]'));

        if (this.applyBtn) this.applyBtn.addEventListener('click', (e) => this.onApply(e));
        if (this.clearBtn) this.clearBtn.addEventListener('click', (e) => this.onClear(e));

        if (this.minRange) {
          this.minRange.addEventListener('input', () => {
            this.updateRangeDisplay();
            this.updateRangeProgress();
          });
        }
        if (this.maxRange) {
          this.maxRange.addEventListener('input', () => {
            this.updateRangeDisplay();
            this.updateRangeProgress();
          });
        }

        this.sortChecks.forEach((chk) => {
          chk.addEventListener('change', () => {
            if (chk.checked) {
              this.sortChecks.forEach((other) => {
                if (other !== chk) other.checked = false;
              });
            }
          });
        });

        this.setupRangeDrag();
        this.restoreRangeFromURL();
        this.updateRangeDisplay();
        this.updateRangeProgress();
      }

      restoreRangeFromURL() {
        if (!this.minRange || !this.maxRange) return;
        const url = new URL(window.location.href);
        const minName = this.minRange.name;
        const maxName = this.maxRange.name;
        if (minName) {
          const mv = url.searchParams.get(minName);
          if (mv !== null && mv !== '') this.minRange.value = mv;
        }
        if (maxName) {
          const xv = url.searchParams.get(maxName);
          if (xv !== null && xv !== '') this.maxRange.value = xv;
        }
        let min = parseFloat(this.minRange.value || '0');
        let max = parseFloat(this.maxRange.value || (this.maxRange.max || '0'));
        if (!isNaN(min) && !isNaN(max) && min > max) this.maxRange.value = String(min);
      }

      setupRangeDrag() {
        if (!this.rangeWrap || !this.minRange || !this.maxRange) return;

        this._drag = { active: null, rect: null };

        const onPointerDown = (ev) => {
          const rect = this.rangeWrap.getBoundingClientRect();
          const minAttr = parseFloat(this.minRange.min || '0');
          const maxAttr = parseFloat(this.maxRange.max || '100');
          const span = Math.max(maxAttr - minAttr, 1);
          const minVal = Math.min(Math.max(parseFloat(this.minRange.value || '0'), minAttr), maxAttr);
          const maxVal = Math.min(Math.max(parseFloat(this.maxRange.value || String(maxAttr)), minAttr), maxAttr);
          const minX = rect.left + ((minVal - minAttr) / span) * rect.width;
          const maxX = rect.left + ((maxVal - minAttr) / span) * rect.width;
          const x = ev.clientX ?? (ev.touches && ev.touches[0]?.clientX);
          if (x == null) return;
          const target = Math.abs(x - minX) <= Math.abs(x - maxX) ? 'min' : 'max';
          this._drag.active = target;
          this._drag.rect = rect;
          try {
            this.rangeWrap.setPointerCapture?.(ev.pointerId);
          } catch (_) {}
          this.updateHandleFromClientX(target, x);
          document.addEventListener('pointermove', onPointerMove);
          document.addEventListener('pointerup', onPointerUp);
          ev.preventDefault();
        };

        const onPointerMove = (ev) => {
          if (!this._drag.active) return;
          const x = ev.clientX ?? (ev.touches && ev.touches[0]?.clientX);
          if (x == null) return;
          this.updateHandleFromClientX(this._drag.active, x);
          ev.preventDefault();
        };

        const onPointerUp = (ev) => {
          try {
            this.rangeWrap.releasePointerCapture?.(ev.pointerId);
          } catch (_) {}
          this._drag.active = null;
          document.removeEventListener('pointermove', onPointerMove);
          document.removeEventListener('pointerup', onPointerUp);
        };

        this.rangeWrap.addEventListener('pointerdown', onPointerDown, { passive: false });
      }

      updateHandleFromClientX(which, clientX) {
        const rect = this._drag?.rect;
        if (!rect) return;

        const minAttr = parseFloat(this.minRange.min || '0');
        const maxAttr = parseFloat(this.maxRange.max || '100');
        const span = Math.max(maxAttr - minAttr, 1);

        let perc = (clientX - rect.left) / rect.width;
        perc = Math.max(0, Math.min(1, perc));
        let val = minAttr + perc * span;
        val = Math.round(val);

        let min = parseFloat(this.minRange.value || '0');
        let max = parseFloat(this.maxRange.value || String(maxAttr));

        if (which === 'min') {
          val = Math.min(val, max);
          this.minRange.value = String(val);
        } else {
          val = Math.max(val, min);
          this.maxRange.value = String(val);
        }

        this.updateRangeDisplay();
        this.updateRangeProgress();
      }

      updateRangeProgress() {
        if (!this.minRange || !this.maxRange) return;
        const wrap = this.rangeWrap || this.querySelector('.collection-filter__price-range');
        if (!wrap) return;

        const minAttr = parseFloat(this.minRange.min || '0');
        const maxAttr = parseFloat(this.maxRange.max || '100');
        const minVal = Math.min(Math.max(parseFloat(this.minRange.value || '0'), minAttr), maxAttr);
        const maxVal = Math.min(Math.max(parseFloat(this.maxRange.value || String(maxAttr)), minAttr), maxAttr);
        const span = Math.max(maxAttr - minAttr, 1);
        const minPerc = ((minVal - minAttr) / span) * 100;
        const maxPerc = ((maxVal - minAttr) / span) * 100;

        wrap.style.setProperty('--min', minPerc + '%');
        wrap.style.setProperty('--max', maxPerc + '%');
      }

      updateRangeDisplay() {
        if (!this.minRange || !this.maxRange) return;

        let min = parseFloat(this.minRange.value);
        let max = parseFloat(this.maxRange.value);

        if (isNaN(min)) min = 0;
        if (isNaN(max)) max = parseFloat(this.maxRange.max || '0');

        if (min > max) {
          if (this._drag?.active === 'min' || this.minRange === document.activeElement) {
            this.maxRange.value = min;
            max = min;
          } else {
            this.minRange.value = max;
            min = max;
          }
        }

        if (this.minValueDisplay) this.minValueDisplay.textContent = formatCurrencyNumber(min);
        if (this.maxValueDisplay) this.maxValueDisplay.textContent = formatCurrencyNumber(max);
      }

      buildUrl() {
        const base = new URL(window.location.origin + window.location.pathname);
        let firstAddUrlSeen = false;

        this.checkboxInputs
          .filter((i) => i.checked)
          .forEach((input) => {
            if (input.dataset.addUrl) {
              const temp = new URL(input.dataset.addUrl, window.location.origin);
              if (!firstAddUrlSeen) {
                base.pathname = temp.pathname;
                firstAddUrlSeen = true;
              }
              for (const [k, v] of temp.searchParams.entries()) {
                base.searchParams.append(k, v);
              }
            } else if (input.name) {
              base.searchParams.append(input.name, input.value);
            }
          });

        if (this.minRange && this.maxRange) {
          if (this.minRange.name) base.searchParams.set(this.minRange.name, this.minRange.value);
          if (this.maxRange.name) base.searchParams.set(this.maxRange.name, this.maxRange.value);
        }

        const checkedMobile = this.sortChecks?.find((c) => c.checked);
        const desktopSelect = document.getElementById('SortBy');
        let chosenSort = null;

        if (checkedMobile) {
          chosenSort = checkedMobile.value;
        } else if (desktopSelect && desktopSelect.value) {
          chosenSort = desktopSelect.value;
        } else {
          const current = new URL(window.location.href);
          chosenSort = current.searchParams.get('sort_by');
        }

        if (chosenSort && this.defaultSort && chosenSort !== this.defaultSort) {
          base.searchParams.set('sort_by', chosenSort);
        } else {
          base.searchParams.delete('sort_by');
        }

        if (this.sectionId) base.searchParams.set('section_id', this.sectionId);
        return base;
      }

      async onApply(e) {
        e.preventDefault();
        const base = this.buildUrl();

        await updateCollectionFromUrl(base);

        const pushUrl = new URL(base.toString());
        pushUrl.searchParams.delete('section_id');
        window.history.pushState({}, '', pushUrl.toString());

        document.querySelector('.collection-filters')?.classList.remove('open_filter');
        document.querySelector('.collection_overlay')?.classList.remove('open_filter');
        unlockBodyScroll();

        window.edProductCard?.();
      }

      async onClear(e) {
        e.preventDefault();
        const base = new URL(window.location.origin + window.location.pathname);
        if (this.sectionId) base.searchParams.set('section_id', this.sectionId);

        await updateCollectionFromUrl(base);

        const pushUrl = new URL(base.toString());
        pushUrl.searchParams.delete('section_id');
        window.history.pushState({}, '', pushUrl.toString());

        document.querySelector('.collection-filters')?.classList.remove('open_filter');
        document.querySelector('.collection_overlay')?.classList.remove('open_filter');
        unlockBodyScroll();

        window.edProductCard?.();
      }
    }
    customElements.define('collection-filters', CollectionFilters);
  }

  async function updateCollectionFromUrl(urlLike, keepDrawerState = false) {
    const cf = document.querySelector('collection-filters');

    const openIndexes = Array.from(cf?.querySelectorAll('accordion-toggle') || [])
      .map((el, i) => (el.classList.contains('accordion--open') ? i : -1))
      .filter((i) => i !== -1);

    const url = new URL(urlLike instanceof URL ? urlLike.toString() : urlLike, window.location.origin);
    const sectionId = cf?.getAttribute('data-section-id') || null;
    if (sectionId) url.searchParams.set('section_id', sectionId);

    const resp = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    const html = await resp.text();
    const temp = document.createElement('div');
    temp.innerHTML = html;

    const newInner = temp.querySelector('.collection-inner');
    const currentInner = document.querySelector('.collection-inner');
    if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;

    const newFilters = temp.querySelector('collection-filters');
    const currentFilters = document.querySelector('collection-filters');
    if (newFilters && currentFilters) currentFilters.replaceWith(newFilters);

    const updatedAccordions = document.querySelectorAll('collection-filters accordion-toggle');
    openIndexes.forEach((i) => {
      if (updatedAccordions[i]) updatedAccordions[i].classList.add('accordion--open');
    });

    const formEl = document.querySelector('collection-filters form.collection-filters__content');
    const defaultSort = formEl?.dataset.sortDefault || '';
    const sortParam = url.searchParams.get('sort_by');
    const effectiveSort = sortParam || defaultSort;

    const sortSelect = document.getElementById('SortBy');
    if (sortSelect) sortSelect.value = effectiveSort;

    const mobileChecks = document.querySelectorAll('collection-filters input[type="checkbox"][data-sort-mobile]');
    mobileChecks.forEach((c) => {
      c.checked = c.value === effectiveSort;
    });

    const cfNew = document.querySelector('collection-filters');
    const minInput = cfNew?.querySelector('input[data-min-value]');
    const maxInput = cfNew?.querySelector('input[data-max-value]');
    if (minInput && maxInput) {
      const minName = minInput.name;
      const maxName = maxInput.name;
      const minParamVal = url.searchParams.get(minName);
      const maxParamVal = url.searchParams.get(maxName);

      if (minParamVal !== null) minInput.value = minParamVal;
      if (maxParamVal !== null) maxInput.value = maxParamVal;

      cfNew.updateRangeDisplay?.();
      cfNew.updateRangeProgress?.();
    }

    observeCollectionFilters();

    if (!keepDrawerState) {
      document.querySelector('.collection-filters')?.classList.remove('open_filter');
      document.querySelector('.collection_overlay')?.classList.remove('open_filter');
      unlockBodyScroll();
    }

    initLoadMore();
    window.edProductCard?.();
  }

  function handleSortChange(e) {
    const select = e.target;
    if (!select || select.id !== 'SortBy') return;

    const baseUrl = new URL(window.location.origin + window.location.pathname);
    const current = new URL(window.location.href);

    current.searchParams.forEach((v, k) => {
      if (k !== 'section_id' && k !== 'page' && k !== 'sort_by') {
        baseUrl.searchParams.set(k, v);
      }
    });

    baseUrl.searchParams.set('sort_by', select.value);
    baseUrl.searchParams.delete('page');

    updateCollectionFromUrl(baseUrl).then(() => {
      const pushUrl = new URL(baseUrl.toString());
      pushUrl.searchParams.delete('section_id');
      window.history.pushState({}, '', pushUrl.toString());
    });
  }

  document.addEventListener('change', (e) => {
    if (e.target && e.target.id === 'SortBy') handleSortChange(e);
  });

  window.addEventListener('popstate', () => {
    updateCollectionFromUrl(window.location.href, true);
    const formEl = document.querySelector('collection-filters form.collection-filters__content');
    const defaultSort = formEl?.dataset.sortDefault || '';
    const params = new URL(window.location.href).searchParams;
    const sv = params.get('sort_by') || defaultSort;

    const sortSelect = document.getElementById('SortBy');
    if (sortSelect) sortSelect.value = sv;

    const mobileChecks = document.querySelectorAll('collection-filters input[type="checkbox"][data-sort-mobile]');
    mobileChecks.forEach((c) => {
      c.checked = c.value === sv;
    });
  });

  document.addEventListener('click', (ev) => {
    const openBtn = ev.target.closest('.icon_filter_label');
    const closeBtn = ev.target.closest('.filter_close');
    const overlayClick = ev.target.closest('.collection_overlay');

    if (openBtn) {
      document.querySelector('.collection-filters')?.classList.add('open_filter');
      document.querySelector('.collection_overlay')?.classList.add('open_filter');
      lockBodyScroll();
    }
    if (closeBtn || overlayClick) {
      document.querySelector('.collection-filters')?.classList.remove('open_filter');
      document.querySelector('.collection_overlay')?.classList.remove('open_filter');
      unlockBodyScroll();
    }
  });

  function initLoadMore() {
    const btn = document.getElementById('load-more-button');
    const grid = document.querySelector('.collection-products');
    const showingSpan = document.getElementById('showing-count');

    if (!btn || !grid) return;

    btn._bound && btn.removeEventListener('click', btn._bound);

    let isLoading = false;
    let currentPage = parseInt(btn.dataset.currentPage || '1', 10);
    const totalPages = parseInt(btn.dataset.totalPages || '1', 10);
    const itemsPerPage = parseInt(btn.dataset.itemsPerPage || '0', 10);
    const sectionId = btn.dataset.sectionId || null;

    if (currentPage >= totalPages) btn.style.display = 'none';
    else btn.style.display = '';

    const onClick = async () => {
      if (isLoading || currentPage >= totalPages) return;
      isLoading = true;
      const oldText = btn.textContent;
      btn.textContent = 'Loading…';

      const url = new URL(window.location.origin + window.location.pathname);
      const current = new URL(window.location.href);
      current.searchParams.forEach((v, k) => {
        if (k !== 'section_id' && k !== 'page') url.searchParams.set(k, v);
      });
      url.searchParams.set('page', String(currentPage + 1));
      if (sectionId) url.searchParams.set('section_id', sectionId);

      try {
        const resp = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const html = await resp.text();
        const temp = document.createElement('div');
        temp.innerHTML = html;

        const nextGrid = temp.querySelector('.collection-products');
        if (nextGrid) {
          Array.from(nextGrid.children).forEach((node) => {
            grid.appendChild(node.cloneNode(true));
          });

          currentPage += 1;
          btn.dataset.currentPage = String(currentPage);

          if (showingSpan) {
            const shown = grid.children.length;
            showingSpan.textContent = shown;
          }

          if (currentPage >= totalPages) {
            btn.style.display = 'none';
          } else {
            btn.textContent = oldText;
          }

          window.edProductCard?.();
        } else {
          btn.style.display = 'none';
        }
      } catch (e) {
        console.error('Load more failed', e);
        btn.textContent = 'Error - Try again';
        setTimeout(() => (btn.textContent = oldText), 1500);
      } finally {
        isLoading = false;
      }
    };

    btn._bound = onClick;
    btn.addEventListener('click', onClick);
  }

  observeCollectionFilters();
  initLoadMore();
})();

const measureText = (text, font) => {
  const span = document.createElement('span');
  span.style.visibility = 'hidden';
  span.style.position = 'absolute';
  span.style.whiteSpace = 'nowrap';
  span.style.font = font;
  span.textContent = text;
  document.body.appendChild(span);
  const width = span.offsetWidth;
  span.remove();
  return width;
};

const resizeSelect = (el) => {
  const font = window.getComputedStyle(el).font;
  const text = el.options[el.selectedIndex].text;
  el.style.width = measureText(text, font) + 25 + 'px';
};

const init = () => {
  document.querySelectorAll('.dynamic-select').forEach((select) => {
    resizeSelect(select);
    select.addEventListener('change', () => resizeSelect(select));
  });
};

document.addEventListener('DOMContentLoaded', init);



/*
(() => {
  // Accordion element
  if (!customElements.get('accordion-toggle')) {
    class AccordionToggle extends HTMLElement {
      connectedCallback() {
        this.header = this.querySelector('.accordion__header');
        this.content = this.querySelector('.accordion__content');
        if (this.header) {
          this.header.addEventListener('click', () => {
            this.classList.toggle('accordion--open');
            if (this.content) {
              this.style.setProperty('--accordion-content-height', `${this.content.scrollHeight}px`);
            }
          });
        }
      }
    }
    customElements.define('accordion-toggle', AccordionToggle);
  }

  // Collection filters
  if (!customElements.get('collection-filters')) {
    class CollectionFilters extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.getAttribute('data-section-id');
        this.applyBtn = this.querySelector('.filter_action_btn.apply');
        this.clearBtn = this.querySelector('.filter_action_btn.clear');
        this.checkboxInputs = Array.from(this.querySelectorAll('input[type="checkbox"]'));
        this.minRange = this.querySelector('input[data-min-value]');
        this.maxRange = this.querySelector('input[data-max-value]');
        this.minValueDisplay = this.querySelector('[data-min-value-display]');
        this.maxValueDisplay = this.querySelector('[data-max-value-display]');

        if (this.applyBtn) this.applyBtn.addEventListener('click', (e) => this.onApply(e));
        if (this.clearBtn) this.clearBtn.addEventListener('click', (e) => this.onClear(e));
        if (this.minRange) this.minRange.addEventListener('input', () => this.updateRangeDisplay());
        if (this.maxRange) this.maxRange.addEventListener('input', () => this.updateRangeDisplay());
      }

      // Build URL for fetch
      buildUrl() {
        const base = new URL(window.location.origin + window.location.pathname);
        let firstAddUrlSeen = false;

        this.checkboxInputs
          .filter(i => i.checked)
          .forEach(input => {
            if (input.dataset.addUrl) {
              const temp = new URL(input.dataset.addUrl, window.location.origin);
              if (!firstAddUrlSeen) {
                base.pathname = temp.pathname;
                firstAddUrlSeen = true;
              }
              for (const [k, v] of temp.searchParams.entries()) {
                base.searchParams.append(k, v);
              }
            } else {
              if (input.name) base.searchParams.append(input.name, input.value);
            }
          });

        if (this.minRange && this.maxRange) {
          if (this.minRange.name) base.searchParams.set(this.minRange.name, this.minRange.value);
          if (this.maxRange.name) base.searchParams.set(this.maxRange.name, this.maxRange.value);
        }

        if (this.sectionId) base.searchParams.set('section_id', this.sectionId);
        return base;
      }

      // Apply filters
      async onApply(e) {
        e.preventDefault();
        const base = this.buildUrl();

        const openIndexes = Array.from(this.querySelectorAll('accordion-toggle'))
          .map((el, i) => (el.classList.contains('accordion--open') ? i : -1))
          .filter(i => i !== -1);

        const resp = await fetch(base.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const html = await resp.text();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const newInner = tempDiv.querySelector('.collection-inner');
        const currentInner = document.querySelector('.collection-inner');
        if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;

        const newFilters = tempDiv.querySelector('collection-filters');
        const currentFilters = document.querySelector('collection-filters');
        if (newFilters && currentFilters) {
          currentFilters.replaceWith(newFilters); // ✅ replace node itself
        }

        // restore open accordions
        const updatedAccordions = document.querySelectorAll('collection-filters accordion-toggle');
        openIndexes.forEach(i => {
          if (updatedAccordions[i]) updatedAccordions[i].classList.add('accordion--open');
        });

        const pushUrl = new URL(base.toString());
        pushUrl.searchParams.delete('section_id');
        window.history.pushState({}, '', pushUrl.toString());

        document.querySelector('.collection-filters')?.classList.remove('open_filter');
        document.querySelector('.collection_overlay')?.classList.remove('open_filter');
      }

      // Clear filters
      async onClear(e) {
        e.preventDefault();
        const base = new URL(window.location.origin + window.location.pathname);
        if (this.sectionId) base.searchParams.set('section_id', this.sectionId);

        const resp = await fetch(base.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const html = await resp.text();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const newInner = tempDiv.querySelector('.collection-inner');
        const currentInner = document.querySelector('.collection-inner');
        if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;

        const newFilters = tempDiv.querySelector('collection-filters');
        const currentFilters = document.querySelector('collection-filters');
        if (newFilters && currentFilters) {
          currentFilters.replaceWith(newFilters); // ✅ reset everything
        }

        const pushUrl = new URL(base.toString());
        pushUrl.searchParams.delete('section_id');
        window.history.pushState({}, '', pushUrl.toString());

        document.querySelector('.collection-filters')?.classList.remove('open_filter');
        document.querySelector('.collection_overlay')?.classList.remove('open_filter');
      }

      // Update range display
      // Update range display
      updateRangeDisplay() {
        if (!this.minRange || !this.maxRange) return;

        let min = parseFloat(this.minRange.value);
        let max = parseFloat(this.maxRange.value);

        // Optional: enforce min <= max
        if (min > max) {
          if (this.minRange === document.activeElement) {
            this.maxRange.value = min;
            max = min;
          } else {
            this.minRange.value = max;
            min = max;
          }
        }

        if (this.minValueDisplay) this.minValueDisplay.textContent = this.formatCurrency(min);
        if (this.maxValueDisplay) this.maxValueDisplay.textContent = this.formatCurrency(max);

        // Optional: live filtering (uncomment if you want it)
        // this.onApply(new Event('click'));
      }

      // Format currency dynamically from Shopify settings
      formatCurrency(value) {
        // Assuming USD. You can replace 'USD' with your shop currency
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: '{{ shop.currency }}', // Shopify liquid variable
          minimumFractionDigits: 0,
        }).format(value);
      }

    }
    customElements.define('collection-filters', CollectionFilters);
  }

  // Drawer toggle
  document.addEventListener('click', (ev) => {
    const openBtn = ev.target.closest('.icon_filter_label');
    const closeBtn = ev.target.closest('.filter_close');
    const overlayClick = ev.target.closest('.collection_overlay');

    if (openBtn) {
      document.querySelector('.collection-filters')?.classList.add('open_filter');
      document.querySelector('.collection_overlay')?.classList.add('open_filter');
    }
    if (closeBtn || overlayClick) {
      document.querySelector('.collection-filters')?.classList.remove('open_filter');
      document.querySelector('.collection_overlay')?.classList.remove('open_filter');
    }
  });
})();
*/